import classNames from "classnames";
import React, { useRef } from "react";
import { Key } from "ts-key-enum";
import { t } from "ttag";

import { VariantOptionBlockInputTypes } from "../../../constants";
import { IProduct } from "../../../models/catalogue";
import { isoProductID } from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { setSelectedRootProduct } from "../reducers";
import { rootProductSelector } from "../selectors";
import { getDefaultRootProduct } from "../utils";

interface IProps {
    inputType?: string;
}

const handleOptionSelectorClick = (
    product: IProduct,
    onSelectRootProduct: (product: IProduct) => void,
    event: React.MouseEvent<HTMLElement>,
) => {
    event.preventDefault();
    onSelectRootProduct(product);
};

const handleOptionSelectorKeyDown = (
    index: number,
    rootProducts: readonly IProduct[],
    optionRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>,
    onSelectRootProduct: (product: IProduct) => void,
    event: React.KeyboardEvent<Element>,
) => {
    const prevIndex = index - 1;
    const nextIndex = index + 1;

    const prevProduct = rootProducts[prevIndex];
    const nextProduct = rootProducts[nextIndex];

    const prevRef = optionRefs.current[prevIndex];
    const nextRef = optionRefs.current[nextIndex];

    // Left arrow
    if (event.key === Key.ArrowLeft && prevProduct && prevRef) {
        onSelectRootProduct(prevProduct);
        prevRef.focus();
    }

    // Right arrow
    if (event.key === Key.ArrowRight && nextProduct && nextRef) {
        onSelectRootProduct(nextProduct);
        nextRef.focus();
    }
};

const buildOptionSelector = (
    product: IProduct,
    index: number,
    selectedRootProduct: IProduct | null,
    rootProducts: readonly IProduct[],
    optionRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>,
    onSelectRootProduct: (product: IProduct) => void,
) => {
    const isSelected = product.id === selectedRootProduct?.id;
    const classes = classNames({
        "pdp-change-root-product-block__option": true,
        "pdp-change-root-product-block__option--selected": isSelected,
    });
    const tabControlID = `change-root-product-block-${product.id}`;
    const tabPanelID = `root-product-conditional-block-${product.id}`;
    return (
        <button
            key={`${product.id}`}
            ref={(ref) => {
                optionRefs.current[index] = ref;
            }}
            id={tabControlID}
            role="tab"
            aria-selected={isSelected}
            aria-controls={tabPanelID}
            className={classes}
            onClick={(event) =>
                handleOptionSelectorClick(product, onSelectRootProduct, event)
            }
            onKeyDown={(event) =>
                handleOptionSelectorKeyDown(
                    index,
                    rootProducts,
                    optionRefs,
                    onSelectRootProduct,
                    event,
                )
            }
            tabIndex={isSelected ? 0 : -1}
        >
            {product.title}
        </button>
    );
};

export const PDPChangeRootProductBlock = (props: IProps): React.ReactNode => {
    const dispatch = useAppDispatch();
    const rootProducts = useAppSelector(
        (state) => state.configurator.entities.rootProducts,
    );
    const selectedRootProduct = useAppSelector((state) =>
        rootProductSelector(state.configurator),
    );

    const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

    const onChange = (
        e: React.FormEvent<HTMLSelectElement | HTMLInputElement>,
    ) => {
        e.preventDefault();
        const rootProductID = isoProductID.wrap(
            parseInt(e.currentTarget.value, 10),
        );
        const rootProduct = rootProducts.find((p) => {
            return p.id === rootProductID;
        });
        if (rootProduct) {
            dispatch(setSelectedRootProduct(rootProduct));
        }
    };

    // If there's not multiple options for rootProduct (because, for example, we're on a single
    // product PDP), don't show the selector.
    if (rootProducts.length < 2) {
        return null;
    }

    if (props.inputType === VariantOptionBlockInputTypes.DROPDOWN) {
        const value = selectedRootProduct
            ? selectedRootProduct.id
            : getDefaultRootProduct(rootProducts).id;
        const htmlID = "rootProductModel";
        return (
            <div>
                <label htmlFor={htmlID}>{t`Model`}</label>
                <div className="product-option-dropdown">
                    <select
                        id={htmlID}
                        value={isoProductID.unwrap(value)}
                        onChange={onChange}
                    >
                        <optgroup label="Model">
                            {rootProducts.map((product) => {
                                return (
                                    <option
                                        key={`${product.id}`}
                                        value={isoProductID.unwrap(product.id)}
                                    >
                                        {product.title}
                                    </option>
                                );
                            })}
                        </optgroup>
                    </select>
                </div>
            </div>
        );
    }

    const onSelectRootProduct = (product: IProduct) => {
        dispatch(setSelectedRootProduct(product));
    };

    return (
        <nav role="tablist">
            {rootProducts.map((product, i) => {
                return buildOptionSelector(
                    product,
                    i,
                    selectedRootProduct,
                    rootProducts,
                    optionRefs,
                    onSelectRootProduct,
                );
            })}
        </nav>
    );
};
